/**
 * AddTaskModal Component
 * Modal for creating new tasks with form validation
 * Handles task type and priority creation
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  MenuItem,
  Button,
  Box,
  Typography,
  Avatar,
  IconButton,
  CircularProgress,
  Menu,
  Tooltip
} from '@mui/material';
import {
  Close,
  Add,
  Check,
  Clear,
  Flag,
  Repeat,
  InsertDriveFile,
  Edit,
  Delete
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTicketDropdowns, fetchProjectDropdownsByUser, createTicketType, createTicketPriority, createTicketSprint, updateTicketDropdown, deleteTicketDropdown, clearError } from '../../store';
import { createTicketLabel } from '../../store/ticketSlice';
import { isStorageQuotaExceededError, uploadFile } from '../../store/fileSlice';
import { COLORS } from '../../shared/colors';
import { USER_ROLES, TASK_TYPE, TASK_STATUS, CHARACTER_LIMITS, TOAST_MESSAGES, CALENDAR_TASK_TYPE_COLOR_MAP, PROJECT_TYPES, RESTRICTED_NUMBER_KEYS } from '../../shared/appConstants';
import { REGEX } from '../../shared/regex';
import { normalizeRichTextHtml, prepareContentForEditing } from '../../shared/htmlUtils';
import { ICONS } from '../../shared/icons';
import { timeStringToMinutes, isValidTimeFormat, validateTimeDigitLimits, minutesToTimeString, buildDateTimeString, getLocalDatePart } from '../../shared/timeUtils';
import { applyCreateTaskFormDraft, buildCreateTaskFormDraft, clearDraftFields, getCreateTaskDraftId, getCreateTaskFormDraftFieldName, hasCreateTaskChecklistContent, loadDraft, loadStructuredDraft, saveStructuredDraft } from '../../shared/autoSaveUtils';
import { getEntityId, normalizeComparableValue, normalizeDateValue, normalizeIdList, stableStringify } from '../../shared/formNormalizationUtils';
import { buildLimitedPasteValue, clampTextToLimit, stringToColor, getPriorityColor } from '../../shared/commonFunctions';
import CommentBox from './CommentBox';
import ChecklistManager from './ChecklistManager';
import { addTaskModalStyles } from '../../shared/globalSX';
import OverflowTooltip from '../../shared/OverflowTooltip';
import CustomColorPopover from '../shared/CustomColorPopover';
import './TaskModal.scss';
import { getStatusIcon, getTaskTypeIcon } from '../../shared/iconUtils';
import { isRecurrenceComplete, normalizeRecurrenceConfig } from '../../shared/recurrenceUtils';
import RecurrencePanel from './RecurrencePanel';
import DatePickerModal from '../../shared/DatePickerModal';
import { format12h } from '../../shared/TimePickerModal';

const hasRichTextContent = (content = '') => {
  if (typeof content !== 'string' || !content) return false;

  const textContent = content
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .trim();

  return Boolean(textContent);
};

const shouldShowDraftField = (content, draftId, fieldName) => {
  if (hasRichTextContent(content)) return true;
  if (!draftId || !fieldName) return false;

  return hasRichTextContent(loadDraft(draftId, fieldName));
};

const normalizeChecklistForComparison = (value) => {
  if (value && !Array.isArray(value) && Array.isArray(value.checklists)) {
    return normalizeComparableValue({ checklists: value.checklists });
  }

  if (!Array.isArray(value) || value.length === 0) {
    return { checklists: [] };
  }

  if (value[0] && Object.prototype.hasOwnProperty.call(value[0], 'checklists')) {
    return normalizeComparableValue(value[0]);
  }

  return normalizeComparableValue({ checklists: value });
};

const normalizeTaskFormForComparison = (data = {}) => ({
  title: data.title || '',
  taskType: String(getEntityId(data.taskType)),
  priority: String(getEntityId(data.priority)),
  status: String(getEntityId(data.status)),
  assignees: normalizeIdList(data.assignees),
  project: String(getEntityId(data.project)),
  dueDate: normalizeDateValue(data.dueDate),
  startDate: normalizeDateValue(data.startDate),
  description: normalizeRichTextHtml(data.description || ''),
  attachments: (data.attachments || []).map((file) => ({
    key: file?.key || '',
    url: file?.url || ''
  })),
  labels: normalizeIdList(data.labels),
  sprint: String(getEntityId(data.sprint)),
  acceptanceCriteria: normalizeRichTextHtml(data.acceptanceCriteria || ''),
  checklist: normalizeChecklistForComparison(data.checklist),
  estimateTime: isValidTimeFormat(data.estimateTime)
    ? String(timeStringToMinutes(data.estimateTime))
    : (data.estimateTime || ''),
  is_recurring: Boolean(data.is_recurring),
  recurrence_config: normalizeRecurrenceConfig(data.recurrence_config)
});

const AddTaskModal = ({ open, onClose, onSubmit, parentTicketId, selectedProjectId, preselectedStatusId, preselectedSprintId, preselectedSprintName, selectedDate, userRole, taskData, isEditMode = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { dropdowns, dropdownsLoading, projectDropdownsByUser, projectDropdownsByUserLoading } = useSelector((state) => state.tickets);

  const { user } = useSelector((state) => state.auth);
  const canManageTicketAttributes = userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.PM || user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.PM;

  const [isCreatingTaskType, setIsCreatingTaskType] = useState(false);
  const [newTaskTypeName, setNewTaskTypeName] = useState('');
  const [isCreatingPriority, setIsCreatingPriority] = useState(false);
  const [newPriorityName, setNewPriorityName] = useState('');
  const [newPriorityColor, setNewPriorityColor] = useState(COLORS.primary);
  const [isCreatingLabel, setIsCreatingLabel] = useState(false);
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState(COLORS.primary);
  const [isCreatingSprint, setIsCreatingSprint] = useState(false);
  const [newSprintName, setNewSprintName] = useState('');
  const [newSprintColor, setNewSprintColor] = useState(COLORS.primary);
  const [editingTaskType, setEditingTaskType] = useState(null);
  const [editingPriority, setEditingPriority] = useState(null);
  const [editingLabel, setEditingLabel] = useState(null);
  const [editingSprint, setEditingSprint] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [createLabelAnchor, setCreateLabelAnchor] = useState(null);
  const [editLabelAnchor, setEditLabelAnchor] = useState(null);
  const [createSprintAnchor, setCreateSprintAnchor] = useState(null);
  const [editSprintAnchor, setEditSprintAnchor] = useState(null);
  const [createPriorityAnchor, setCreatePriorityAnchor] = useState(null);
  const [editPriorityAnchor, setEditPriorityAnchor] = useState(null);

  const abortControllerRef = useRef(null);
  const descriptionCommentBoxRef = useRef(null);
  const createLocksRef = useRef({
    taskType: false,
    priority: false,
    label: false,
    sprint: false
  });
  const deleteLocksRef = useRef({
    taskType: false,
    priority: false
  });
  const [isCreatingTaskTypeSubmitting, setIsCreatingTaskTypeSubmitting] = useState(false);
  const [isCreatingPrioritySubmitting, setIsCreatingPrioritySubmitting] = useState(false);
  const [isCreatingLabelSubmitting, setIsCreatingLabelSubmitting] = useState(false);
  const [isCreatingSprintSubmitting, setIsCreatingSprintSubmitting] = useState(false);

  const [dateError, setDateError] = useState(''); // eslint-disable-line no-unused-vars
  const [timeError, setTimeError] = useState('');
  const [storyPointsError, setStoryPointsError] = useState('');
  const [assigneeError, setAssigneeError] = useState('');
  const [titleError, setTitleError] = useState('');
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    taskType: '',
    priority: '',
    status: '',
    assignees: [],
    project: '',
    dueDate: '',
    startDate: getLocalDatePart(new Date()),
    startTime: '',
    dueTime: '',
    description: '',
    attachments: [],
    labels: [],
    sprint: '',
    acceptanceCriteria: '',
    checklist: [],
    estimateTime: '',
    storyPoints: '',
    is_recurring: false,
    recurrence_config: null,
    next_rep_on: null
  });

  const [dueDatePickerAnchor, setDueDatePickerAnchor] = useState(null);

  const selectedProjectObj = projectDropdownsByUser?.find(p => p.id === (typeof formData.project === 'object' ? formData.project?.id : formData.project) || p.id === selectedProjectId);
  const isScrum = (selectedProjectObj?.project_type || '').toUpperCase() === PROJECT_TYPES.SCRUM.toUpperCase();
  const [originalEditFormData, setOriginalEditFormData] = useState(null);
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const [showAC, setShowAC] = useState(false);
  const [showTimeEstimate, setShowTimeEstimate] = useState(false);
  const [showStoryPoints, setShowStoryPoints] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const [showRecurrence, setShowRecurrence] = useState(false);
  
  // Menu anchors for dropdowns
  const [menuAnchors, setMenuAnchors] = useState({
    status: null,
    labels: null,
    sprint: null,
    taskType: null,
    priority: null,
    assignee: null,
    more: null
  });

  const handleMenuOpen = (event, field) => {
    setMenuAnchors(prev => ({ ...prev, [field]: event.currentTarget }));
  };

  const handleMenuClose = (field) => {
    setMenuAnchors(prev => ({ ...prev, [field]: null }));
  };

  const handleMenuSelect = (field, value) => {
    handleInputChange(field, value);
    handleMenuClose(field);
  };

  const normalizedFormProjectId = typeof formData.project === 'object' ? formData.project?.id : formData.project;
  const createTaskDraftProjectId = selectedProjectId || normalizedFormProjectId || projectDropdownsByUser?.[0]?.id || '';
  const draftId = isEditMode && taskData?.id ? taskData.id : getCreateTaskDraftId(createTaskDraftProjectId);
  const createTaskFormDraftField = getCreateTaskFormDraftFieldName();

  const getCreateTaskDefaultFormData = useCallback(() => {
    const defaultTaskType = (dropdowns?.types || []).find(type => type.name === TASK_TYPE.TASK)?.id
      || dropdowns?.types?.[0]?.id
      || '';
    const defaultPriority = dropdowns?.priorities?.[0]?.id || '';
    const defaultStatus = preselectedStatusId
      || (dropdowns?.statuses || []).find(status => status.name === TASK_STATUS.TO_DO)?.id
      || dropdowns?.statuses?.[0]?.id
      || '';
    const defaultProject = selectedProjectId || projectDropdownsByUser?.[0]?.id || '';
    return {
      title: '',
      taskType: defaultTaskType,
      priority: defaultPriority,
      status: defaultStatus,
      assignees: [],
      project: defaultProject,
      dueDate: '',
      startDate: selectedDate || getLocalDatePart(new Date()),
      startTime: '',
      dueTime: '',
      description: '',
      attachments: [],
      labels: [],
      sprint: preselectedSprintId || '',
      acceptanceCriteria: '',
      checklist: [],
      estimateTime: '',
      storyPoints: '',
      is_recurring: false,
      recurrence_config: null,
      next_rep_on: null
    };
  }, [dropdowns?.types, dropdowns?.priorities, dropdowns?.statuses, projectDropdownsByUser, preselectedStatusId, preselectedSprintId, selectedProjectId, selectedDate]);

  useEffect(() => {
    if (open) {
      const initialDescription = isEditMode ? taskData?.description || '' : '';
      const initialAcceptanceCriteria = isEditMode
        ? taskData?.acceptanceCriteria || taskData?.acceptance_criteria || ''
        : '';

      // Clear any previous errors when modal opens
      dispatch(clearError());
      
      // Only fetch project dropdowns if not already loaded
      if (!projectDropdownsByUser || projectDropdownsByUser.length === 0) {
        dispatch(fetchProjectDropdownsByUser());
      }
      
      // Only fetch ticket dropdowns if missing critical data
      const hasDropdownData = dropdowns && (dropdowns.types?.length > 0 || dropdowns.priorities?.length > 0 || dropdowns.statuses?.length > 0);
      const hasAssignees = dropdowns?.assignees && dropdowns.assignees.length > 0;
      if (!hasDropdownData || !hasAssignees) {
        if (selectedProjectId) {
          dispatch(fetchTicketDropdowns(selectedProjectId));
        } else {
          dispatch(fetchTicketDropdowns());
        }
      }
      
      setDefaultsApplied(false);
      setShowDescription(shouldShowDraftField(initialDescription, draftId, 'description'));
      setShowAC(shouldShowDraftField(initialAcceptanceCriteria, draftId, 'acceptanceCriteria'));
      setShowRecurrence(Boolean(isEditMode && taskData?.is_recurring));

      // Pre-populate form with taskData when in edit mode
      if (isEditMode && taskData) {
        const editFormData = {
          title: taskData.title || '',
          taskType: taskData.taskType || taskData.type?.id || '',
          priority: taskData.priority?.id || taskData.priority || '',
          status: taskData.status?.id || taskData.status || '',
          assignees: Array.isArray(taskData.assignees) ? taskData.assignees.map(a => typeof a === 'object' ? a.id : a) : [],
          project: taskData.project?.id || taskData.project || selectedProjectId || '',
          dueDate: taskData.dueDate || getLocalDatePart(taskData.end_date),
          startDate: taskData.startDate || getLocalDatePart(taskData.start_date),
          description: taskData.description || '',
          attachments: taskData.attachments || [],
          labels: (taskData.labels || []).map(l => typeof l === 'object' ? l.id : l),
          sprint: taskData.sprint?.id || taskData.sprint || preselectedSprintId || '',
          acceptanceCriteria: taskData.acceptanceCriteria || taskData.acceptance_criteria || '',
          checklist: taskData.checklist || [],
          estimateTime: minutesToTimeString(taskData.estimate_time_in_minutes || 0),
          storyPoints: taskData.story_points || taskData.sp || taskData.storyPoints || '',
          is_recurring: Boolean(taskData.is_recurring),
          recurrence_config: taskData.is_recurring ? normalizeRecurrenceConfig(taskData.recurrence_config) : null,
          next_rep_on: taskData.next_rep_on || null
        };
        setFormData(editFormData);
        setOriginalEditFormData(editFormData);
        setDefaultsApplied(true);
      } else if (!isEditMode) {
        setOriginalEditFormData(null);
        // For create mode, set start date from selectedDate ONLY if explicitly provided (from calendar view)
        // Otherwise use today's date (for board view, list view, etc.)
        setFormData(prev => ({
          ...prev,
          startDate: selectedDate || getLocalDatePart(new Date())
        }));
      }
    }
  }, [open, dispatch, selectedProjectId, preselectedSprintId, projectDropdownsByUser, dropdowns, selectedDate, isEditMode, draftId, createTaskFormDraftField, taskData]);

  useEffect(() => {
    if (!open || defaultsApplied || !dropdowns || dropdownsLoading || projectDropdownsByUserLoading) return;
    const data = dropdowns || {};
    if (data.types?.length > 0 || data.priorities?.length > 0 || data.statuses?.length > 0 || projectDropdownsByUser?.length > 0 || data.assignees?.length > 0) {
      if (!isEditMode) {
        const defaultFormData = getCreateTaskDefaultFormData();
        const savedDraft = loadStructuredDraft(draftId, createTaskFormDraftField);
        const restoredFormData = applyCreateTaskFormDraft(defaultFormData, savedDraft);
        const mergedFormData = {
          ...restoredFormData,
          ...(preselectedStatusId ? { status: preselectedStatusId } : {}),
          ...(isScrum ? { sprint: preselectedSprintId || '' } : {})
        };

        setFormData(mergedFormData);
        setShowDescription(shouldShowDraftField(mergedFormData.description, draftId, 'description'));
        setShowAC(shouldShowDraftField(mergedFormData.acceptanceCriteria, draftId, 'acceptanceCriteria'));
        setShowTimeEstimate(Boolean(mergedFormData.estimateTime));
        setShowStoryPoints(Boolean(mergedFormData.storyPoints));
        setShowChecklist(hasCreateTaskChecklistContent(mergedFormData.checklist));
      } else {
        setFormData((prev) => {
          const next = { ...prev };
          let changed = false;
          if (!next.taskType && (data.types?.length || 0) > 0) { 
            const taskType = data.types.find(type => type.name === TASK_TYPE.TASK);
            next.taskType = taskType ? taskType.id : data.types[0].id; 
            changed = true; 
          }
          if (!next.priority && (data.priorities?.length || 0) > 0) { next.priority = data.priorities[0].id; changed = true; }
          if (!next.status && (data.statuses?.length || 0) > 0) { 
            if (preselectedStatusId) {
              next.status = preselectedStatusId;
            } else {
              const todoStatus = data.statuses.find(s => s.name === TASK_STATUS.TO_DO);
              next.status = todoStatus ? todoStatus.id : data.statuses[0].id;
            }
            changed = true; 
          }
          if (!next.project) {
            if (selectedProjectId) {
              next.project = selectedProjectId;
              changed = true;
            } else if ((projectDropdownsByUser?.length || 0) > 0) {
              next.project = projectDropdownsByUser[0].id;
              changed = true;
            }
          }
          if (selectedDate && next.startDate !== selectedDate) {
            next.startDate = selectedDate;
            changed = true;
          }
          if (changed) {
            setOriginalEditFormData(next);
            return next;
          }
          return prev;
        });
      }
      setDefaultsApplied(true);
    }
  }, [open, defaultsApplied, dropdowns, dropdownsLoading, projectDropdownsByUser, projectDropdownsByUserLoading, selectedProjectId, preselectedStatusId, preselectedSprintId, isScrum, selectedDate, isEditMode, draftId, createTaskFormDraftField, getCreateTaskDefaultFormData]);

  useEffect(() => {
    if (!open || isEditMode || !defaultsApplied) return;

    const defaultFormData = getCreateTaskDefaultFormData();
    const draftData = buildCreateTaskFormDraft(formData, defaultFormData);
    saveStructuredDraft(draftId, createTaskFormDraftField, draftData);
  }, [open, isEditMode, defaultsApplied, formData, draftId, createTaskFormDraftField, getCreateTaskDefaultFormData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      // Cancel any ongoing requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      // Reset submitting state defensively
      setIsSubmitting(false);
      setOriginalEditFormData(null);
      // Reset form to clear any errors
      resetForm();
    }
  }, [open]);

  const handleInputChange = (field, value) => {
    const nextValue = field === 'title'
      ? clampTextToLimit(value, CHARACTER_LIMITS.TICKET_TITLE)
      : value;

    setFormData(prev => ({
      ...prev,
      [field]: nextValue
    }));

    if (field === 'title') {
      validateTitle(nextValue);
      return;
    }

    if (field === 'assignees') {
      setAssigneeError(Array.isArray(nextValue) && nextValue.length > 0 ? '' : assigneeError);
    }

    if (field === 'storyPoints') {
      validateStoryPoints(nextValue);
    }
    
    // Validate dates and times
    if (field === 'startDate' || field === 'dueDate' || field === 'startTime' || field === 'dueTime') {
      validateDates(
        field === 'startDate' ? nextValue : formData.startDate, 
        field === 'dueDate' ? nextValue : formData.dueDate,
        field === 'startTime' ? nextValue : formData.startTime,
        field === 'dueTime' ? nextValue : formData.dueTime
      );
    }

    // Validate time format when estimate time changes
    if (field === t('taskModal.estimateTime')) {
      validateTimeFormat(nextValue);
    }
  };

  const handleTitlePaste = (event) => {
    const pastedText = event.clipboardData?.getData('text') || '';
    const { value } = buildLimitedPasteValue({
      currentValue: formData.title,
      pastedText,
      selectionStart: event.target.selectionStart,
      selectionEnd: event.target.selectionEnd,
      limit: CHARACTER_LIMITS.TICKET_TITLE
    });

    event.preventDefault();
    handleInputChange('title', value);
  };

  const validateDates = (startDate, endDate, startTime = formData.startTime, dueTime = formData.dueTime) => {
    if (startDate && endDate) {
      const sDate = new Date(startDate);
      const eDate = new Date(endDate);
      sDate.setHours(0, 0, 0, 0);
      eDate.setHours(0, 0, 0, 0);
      
      if (sDate > eDate) {
        setDateError(isEditMode ? t('taskModal.dueDateBeforeStartDate') : t('taskModal.dueDateCannotBeBeforeToday'));
        return false;
      } else if (sDate.getTime() === eDate.getTime()) {
        let effectiveStartTime = startTime;
        if (!effectiveStartTime && !isEditMode) {
          const now = new Date();
          effectiveStartTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
        }
        if (effectiveStartTime && dueTime) {
          const [startH, startM] = effectiveStartTime.split(':').map(Number);
          const [dueH, dueM] = dueTime.split(':').map(Number);
          if (dueH < startH || (dueH === startH && dueM < startM)) {
            setDateError(t('taskModal.dueTimeBeforeStartTime'));
            return false;
          }
        }
      }
    }
    setDateError('');
    return true;
  };

  const validateTimeFormat = (timeString) => {
    if (validateTimeDigitLimits(timeString)) {
      setTimeError(t('taskModal.numbersCannotExceed5Digits'));
      return false;
    }
    
    if (!isValidTimeFormat(timeString)) {
      setTimeError(t('taskModal.invalidEstimateFormat'));
      return false;
    }
    
    setTimeError('');
    return true;
  };

  const validateTitle = (title) => {
    if ((title || '').length > CHARACTER_LIMITS.TICKET_TITLE) {
      setTitleError(t('taskModal.titleCannotExceed255Chars', 'Task title cannot exceed 255 characters'));
      return false;
    }

    setTitleError('');
    return true;
  };

  const validateStoryPoints = (storyPointsValue = formData.storyPoints) => {
    const hasValue = storyPointsValue !== '' && storyPointsValue !== null;

    if (!hasValue) {
      setStoryPointsError('');
      return true;
    }

    const sp = Number(storyPointsValue);
    if (isNaN(sp) || sp < 1 || sp > 40) {
      setStoryPointsError(t('taskModal.storyPointsInvalid'));
      return false;
    }

    setStoryPointsError('');
    return true;
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    
    const MAX_ATTACHMENTS = 20;
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    const MIN_FILE_SIZE = 1024; // 1KB
    
    const currentAttachmentsCount = formData.attachments?.length || 0;
    if (currentAttachmentsCount + files.length > MAX_ATTACHMENTS) {
      toast.error(t('taskModal.maxAttachmentsError', `Maximum ${MAX_ATTACHMENTS} attachments allowed`));
      event.target.value = '';
      return;
    }

    setIsUploadingFiles(true);
    const uploadedFiles = [];
    
    try {
      for (const file of files) {
        if (file.size > MAX_FILE_SIZE) {
          toast.error(t('taskModal.fileTooLarge', `File ${file.name} is too large. Max size is 10MB`));
          continue;
        }
        if (file.size < MIN_FILE_SIZE) {
          toast.error(t('taskModal.fileTooSmall', `File ${file.name} is too small. Min size is 1KB`));
          continue;
        }
        try {
          const uploadedFile = await dispatch(uploadFile(file)).unwrap();
          uploadedFiles.push(uploadedFile);
        } catch (error) {
          console.error('Failed to upload file:', file.name, error);
          if (!isStorageQuotaExceededError(error)) {
            let errorMessage = t('taskModal.attachmentUploadError');
            if (typeof error === 'string' && error.trim() !== '') {
              errorMessage = error;
            } else if (error && typeof error === 'object' && (error.msg || error.message)) {
              errorMessage = error.msg || error.message;
            }
            toast.error(errorMessage);
          }
        }
      }
      
      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...uploadedFiles]
      }));

      if (uploadedFiles.length > 0) {
        toast.success(t(uploadedFiles.length === 1 ? 'taskModal.attachmentUploadSuccess' : 'taskModal.attachmentsUploadSuccess'));
      }
    } finally {
      setIsUploadingFiles(false);
      event.target.value = '';
    }
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const buildChecklistPayload = (checklistValue) => {
    if (checklistValue && !Array.isArray(checklistValue) && Array.isArray(checklistValue.checklists)) {
      return checklistValue;
    }

    if (!Array.isArray(checklistValue) || checklistValue.length === 0) {
      return { checklists: [] };
    }

    // If checklist is already in nested payload shape, pass through as-is.
    if (
      checklistValue[0] &&
      (Object.prototype.hasOwnProperty.call(checklistValue[0], 'checklists') ||
        (Object.prototype.hasOwnProperty.call(checklistValue[0], 'title') &&
          Object.prototype.hasOwnProperty.call(checklistValue[0], 'items')))
    ) {
      if (Object.prototype.hasOwnProperty.call(checklistValue[0], 'checklists')) {
        return checklistValue[0];
      }
      return { checklists: checklistValue };
    }

    const prioritiesByName = new Map((dropdowns.priorities || []).map((p) => [p.name, p.id]));
    const assigneesByName = new Map((dropdowns.assignees || []).map((a) => [a.name, a.id]));

    const checklistMap = new Map();
    checklistValue.forEach((item) => {
      const title = (item?.checklistTitle || 'Default Checklist').trim() || 'Default Checklist';
      if (!checklistMap.has(title)) {
        checklistMap.set(title, {
          id: crypto.randomUUID(),
          title,
          position: checklistMap.size + 1,
          items: []
        });
      }

      const group = checklistMap.get(title);
      const assigneeName = item?.assignedTo?.name || null;
      const priorityName = item?.priority?.name || null;

      group.items.push({
        id: item?.id || crypto.randomUUID(),
        text: item?.text || '',
        completed: Boolean(item?.completed),
        assignedTo: assigneeName ? (assigneesByName.get(assigneeName) || null) : null,
        dueDate: item?.dueDate || null,
        priority: priorityName ? (prioritiesByName.get(priorityName) || null) : null,
        position: group.items.length + 1
      });
    });

    return { checklists: Array.from(checklistMap.values()) };
  };

  const uploadPendingDescriptionAttachments = async () => {
    const pendingFiles = descriptionCommentBoxRef.current?.getAttachments?.() || [];
    if (pendingFiles.length === 0) {
      return [];
    }

    const uploadedFiles = [];
    for (const file of pendingFiles) {
      // Description editor attachments are uploaded only with the final submit.
      if (file?.key && file?.url) {
        uploadedFiles.push(file);
        continue;
      }

      try {
        const uploadedFile = await dispatch(uploadFile(file)).unwrap();
        uploadedFiles.push(uploadedFile);
      } catch (error) {
        if (!isStorageQuotaExceededError(error)) {
          let errorMessage = t('taskModal.descriptionAttachmentUploadError', 'Failed to upload description attachment. Please try again.');
          if (typeof error === 'string' && error.trim() !== '') {
            errorMessage = error;
          } else if (error && typeof error === 'object' && (error.msg || error.message)) {
            errorMessage = error.msg || error.message;
          }
          toast.error(errorMessage);
        }
        const uploadError = new Error('ATTACHMENT_UPLOAD_ERROR');
        uploadError.isHandled = true;
        throw uploadError;
      }
    }

    return uploadedFiles;
  };

  const handleDeleteTaskType = async (taskType) => {
    if (deleteLocksRef.current.taskType) return;
    deleteLocksRef.current.taskType = true;
    try {
      await dispatch(deleteTicketDropdown({ id: taskType.id, category: 'type' })).unwrap();
      if (formData.taskType === taskType.id) {
        setFormData(prev => ({ ...prev, taskType: '' }));
      }
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.TICKET_ATTRIBUTE_DELETE_FAILED;
      toast.error(errorMessage);
    } finally {
      deleteLocksRef.current.taskType = false;
    }
  };

  const handleDeletePriority = async (priority) => {
    if (deleteLocksRef.current.priority) return;
    deleteLocksRef.current.priority = true;
    try {
      await dispatch(deleteTicketDropdown({ id: priority.id, category: 'priority' })).unwrap();
      if (formData.priority === priority.id) {
        setFormData(prev => ({ ...prev, priority: '' }));
      }
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.TICKET_ATTRIBUTE_DELETE_FAILED;
      toast.error(errorMessage);
    } finally {
      deleteLocksRef.current.priority = false;
    }
  };

  const handleUpdateTaskType = async () => {
    if (editingTaskType?.newName?.trim()) {
      try {
        await dispatch(updateTicketDropdown({ 
          id: editingTaskType.id, 
          name: editingTaskType.newName.trim(), 
          category: 'type',
          projectId: ''
        })).unwrap();
      } catch (error) {
        const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.TASK_TYPE_UPDATE_FAILED;
        toast.error(errorMessage);
      } finally {
        setEditingTaskType(null);
      }
    }
  };

  const handleUpdatePriority = async () => {
    if (editingPriority?.newName?.trim()) {
      try {
        await dispatch(updateTicketDropdown({ 
          id: editingPriority.id, 
          name: editingPriority.newName.trim(), 
          colour: editingPriority.newColor,
          category: 'priority',
          projectId: ''
        })).unwrap();
      } catch (error) {
        const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.PRIORITY_UPDATE_FAILED;
        toast.error(errorMessage);
      } finally {
        setEditingPriority(null);
      }
    }
  };

  const handleUpdateLabel = async () => {
    if (editingLabel?.newName?.trim()) {
      try {
        await dispatch(updateTicketDropdown({ 
          id: editingLabel.id, 
          name: editingLabel.newName.trim(), 
          colour: editingLabel.newColor,
          category: 'labels',
          projectId: ''
        })).unwrap();
      } catch (error) {
        const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.TICKET_ATTRIBUTE_UPDATE_FAILED;
        toast.error(errorMessage);
      } finally {
        setEditingLabel(null);
      }
    }
  };

  const handleDeleteLabel = async (label) => {
    try {
      await dispatch(deleteTicketDropdown({ id: label.id, category: 'labels' })).unwrap();
      if (formData.labels?.includes(label.id)) {
        setFormData(prev => ({
          ...prev,
          labels: prev.labels.filter(id => id !== label.id)
        }));
      }
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.TICKET_ATTRIBUTE_DELETE_FAILED;
      toast.error(errorMessage);
    }
  };

  const handleUpdateSprint = async () => {
    if (editingSprint?.newName?.trim()) {
      try {
        await dispatch(updateTicketDropdown({ 
          id: editingSprint.id, 
          name: editingSprint.newName.trim(), 
          colour: editingSprint.newColor,
          category: 'sprint',
          projectId: ''
        })).unwrap();
      } catch (error) {
        const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.TICKET_ATTRIBUTE_UPDATE_FAILED;
        toast.error(errorMessage);
      } finally {
        setEditingSprint(null);
      }
    }
  };

  const handleDeleteSprint = async (sprint) => {
    try {
      await dispatch(deleteTicketDropdown({ id: sprint.id, category: 'sprint' })).unwrap();
      if (formData.sprint === sprint.id) {
        setFormData(prev => ({ ...prev, sprint: '' }));
      }
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.TICKET_ATTRIBUTE_DELETE_FAILED;
      toast.error(errorMessage);
    }
  };



  const handleCreateTaskTypeConfirm = async () => {
    if (createLocksRef.current.taskType || !newTaskTypeName.trim() || !formData.project) return;

    createLocksRef.current.taskType = true;
    setIsCreatingTaskTypeSubmitting(true);
    try {
      const created = await dispatch(createTicketType({ name: newTaskTypeName, projectId: formData.project })).unwrap();
      if (created?.id) {
        setFormData((prev) => ({ ...prev, taskType: created.id }));
      }
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.TASK_TYPE_CREATE_FAILED;
      toast.error(errorMessage);
    } finally {
      createLocksRef.current.taskType = false;
      setIsCreatingTaskTypeSubmitting(false);
      handleCreateTaskTypeCancel();
    }
  };

  const handleCreateTaskTypeCancel = () => {
    setNewTaskTypeName('');
    setIsCreatingTaskType(false);
  };

  const handleCreatePriorityConfirm = async () => {
    if (createLocksRef.current.priority || !newPriorityName.trim() || !formData.project) return;

    createLocksRef.current.priority = true;
    setIsCreatingPrioritySubmitting(true);
    try {
      const created = await dispatch(createTicketPriority({ name: newPriorityName, colour: newPriorityColor, projectId: formData.project })).unwrap();
      if (created?.id) {
        setFormData((prev) => ({ ...prev, priority: created.id }));
      }
    } catch (error) {
      const errorMessage = (typeof error === 'string' ? error : null) || error?.message || error?.payload || TOAST_MESSAGES.PRIORITY_CREATE_FAILED;
      toast.error(errorMessage);
    } finally {
      createLocksRef.current.priority = false;
      setIsCreatingPrioritySubmitting(false);
      setNewPriorityName('');
      setNewPriorityColor(COLORS.primary);
      setIsCreatingPriority(false);
    }
  };

  const handleCreatePriorityCancel = () => {
    setNewPriorityName('');
    setNewPriorityColor(COLORS.primary);
    setIsCreatingPriority(false);
  };

  const handleCreateLabelConfirm = async () => {
    if (createLocksRef.current.label || !newLabelName.trim() || !formData.project) return;
    createLocksRef.current.label = true;
    setIsCreatingLabelSubmitting(true);
    try {
      const result = await dispatch(createTicketLabel({ name: newLabelName, colour: newLabelColor, projectId: formData.project })).unwrap();
      if (result?.id) handleInputChange('labels', [...(formData.labels || []), result.id]);
    } catch (error) {
      toast.error(error?.message || TOAST_MESSAGES.TICKET_ATTRIBUTE_CREATE_FAILED);
    } finally {
      createLocksRef.current.label = false;
      setIsCreatingLabelSubmitting(false);
      setNewLabelName('');
      setNewLabelColor(COLORS.primary);
      setIsCreatingLabel(false);
    }
  };

  const handleCreateSprintConfirm = async () => {
    if (createLocksRef.current.sprint || !newSprintName.trim() || !formData.project) return;
    createLocksRef.current.sprint = true;
    setIsCreatingSprintSubmitting(true);
    try {
      const result = await dispatch(createTicketSprint({ name: newSprintName, colour: newSprintColor, projectId: formData.project })).unwrap();
      if (result?.id) handleInputChange('sprint', result.id);
    } catch (error) {
      toast.error(error?.message || TOAST_MESSAGES.TICKET_ATTRIBUTE_CREATE_FAILED);
    } finally {
      createLocksRef.current.sprint = false;
      setIsCreatingSprintSubmitting(false);
      setNewSprintName('');
      setNewSprintColor(COLORS.primary);
      setIsCreatingSprint(false);
    }
  };


  const hasEditFormChanges = !isEditMode || (
    originalEditFormData &&
    stableStringify(normalizeTaskFormForComparison(formData)) !== stableStringify(normalizeTaskFormForComparison(originalEditFormData))
  );

  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (isUploadingFiles) {
      toast.error(t('taskModal.uploadingFiles', 'Files are uploading...'));
      return;
    }

    if (!isScrum && (!formData.assignees || formData.assignees.length === 0)) {
      const message = t('taskModal.assigneeRequired', 'Please select at least one assignee');
      setAssigneeError(message);
      return;
    }

    if (isScrum && formData.storyPoints !== '' && formData.storyPoints !== null) {
      if (!validateStoryPoints()) {
        setShowStoryPoints(true);
        return;
      }
    }

    if (isScrum && formData.sprint && !formData.storyPoints) {
      toast.error(t('taskModal.storyPointsRequired'));
      return;
    }

    if (!validateTitle(formData.title)) {
      return;
    }

    if (!isRecurrenceComplete(formData.is_recurring, formData.recurrence_config)) {
      toast.error('Please select both a recurrence frequency and repeat value.');
      return;
    }
    
    // Validate dates and time before submitting
    if (!validateDates(formData.startDate, formData.dueDate, formData.startTime, formData.dueTime)) {
      return;
    }
    
    if (!validateTimeFormat(formData.estimateTime)) {
      return;
    }
    
    setIsSubmitting(true);
    abortControllerRef.current = new AbortController();
    
    try {
      const descriptionAttachments = await uploadPendingDescriptionAttachments();
      const savedAttachments = [
        ...formData.attachments.filter(file => file?.key && file?.url),
        ...descriptionAttachments
      ];

      if (savedAttachments.length > CHARACTER_LIMITS.MAX_ATTACHMENTS) {
        toast.error(t('taskModal.attachmentLimitExceededOnSave'));
        return;
      }

      // Format attachments as array of objects with key and url
      const attachments = savedAttachments.map(file => ({
        key: file.key,
        url: file.url
      }));
      const currentDescription = descriptionCommentBoxRef.current?.getContent?.() ?? formData.description;
      
      const submitData = {
        ...formData,
        startDate: buildDateTimeString(formData.startDate, formData.startTime, { isStartDate: true, isEditMode: isEditMode }),
        dueDate: buildDateTimeString(formData.dueDate, formData.dueTime, { isStartDate: false, isEditMode: isEditMode }),
        has_due_time: Boolean(formData.dueTime),
        ...(isEditMode && taskData?.id ? { id: taskData.id } : {}),
        description: normalizeRichTextHtml(currentDescription),
        attachments: attachments,
        labels: formData.labels || [],
        sprint: (isScrum && selectedProject?.scrum_sprint_id) ? selectedProject.scrum_sprint_id : (formData.sprint || null),
        acceptance_criteria: formData.acceptanceCriteria || '',
        checklist: buildChecklistPayload(formData.checklist),
        parent_ticket_id: parentTicketId || (isEditMode ? (taskData?.parent_ticket_id || taskData?.parent_ticket?.id || null) : null),
        is_recurring: Boolean(formData.is_recurring),
        recurrence_config: formData.is_recurring ? normalizeRecurrenceConfig(formData.recurrence_config) : null
      };
      delete submitData.startTime;
      delete submitData.dueTime;
      delete submitData.next_rep_on;
      
      if (isScrum) {
        submitData.story_points = formData.storyPoints ? Number(formData.storyPoints) : null;
      } else {
        submitData.estimate_time_in_minutes = formData.estimateTime ? timeStringToMinutes(formData.estimateTime) : null;
      }
      
      // Call the onSubmit prop instead of dispatching directly
      if (onSubmit) {
        await onSubmit(submitData);
        descriptionCommentBoxRef.current?.setAttachments?.([]);
        
        // Clear all drafts on successful submit
        clearDraftFields(draftId, [createTaskFormDraftField]);
      }
      
      // Reset form only if request wasn't aborted
      if (!abortControllerRef.current?.signal.aborted) {
        resetForm();
      }
    } catch (error) {
      // Error is handled by the async thunk
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setIsSubmitting(false);
      }
      abortControllerRef.current = null;
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      taskType: '',
      priority: '',
      status: '',
      assignees: [],
      project: '',
      dueDate: '',
      startDate: getLocalDatePart(new Date()),
      startTime: '',
      dueTime: '',
      description: '',
      attachments: [],
      labels: [],
      sprint: '',
      acceptanceCriteria: '',
      checklist: [],
      estimateTime: '',
      storyPoints: '',
      is_recurring: false,
      recurrence_config: null,
      next_rep_on: null
    });
    setIsCreatingTaskType(false);
    setIsCreatingPriority(false);
    setNewTaskTypeName('');
    setNewPriorityName('');
    setIsCreatingLabel(false);
    setNewLabelName('');
    setNewLabelColor(COLORS.primary);
    setIsCreatingSprint(false);
    setNewSprintName('');
    setNewSprintColor(COLORS.primary);
    setEditingTaskType(null);
    setEditingPriority(null);
    setDefaultsApplied(false);
    setDateError('');
    setTimeError('');
    setStoryPointsError('');
    setAssigneeError('');
    setTitleError('');
    setShowDescription(false);
    setShowRecurrence(false);
    setShowAC(false);
    setShowTimeEstimate(false);
    setShowChecklist(false);
  };

  const handleCancel = () => {
    // Cancel any ongoing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    // Clear drafts on cancel
    clearDraftFields(draftId, [createTaskFormDraftField]);
    
    resetForm();
    setIsSubmitting(false);
    onClose();
  };

  // eslint-disable-next-line no-unused-vars
  const getInitials = (name) => {
    if (!name) return '';
    const parts = name.trim().split(' ');
    const initials = parts.slice(0, 2).map(p => p[0]?.toUpperCase()).join('');
    return initials || name[0]?.toUpperCase() || '';
  };

  const getTaskTypeColor = (typeName) => {
    const name = typeName?.toLowerCase() || '';
    if (name.includes('bug')) return CALENDAR_TASK_TYPE_COLOR_MAP.BUG;
    if (name.includes('feature')) return CALENDAR_TASK_TYPE_COLOR_MAP.FEATURE;
    if (name.includes('improvement')) return COLORS.indigo[500];
    if (name.includes('feedback')) return COLORS.purple[500];
    if (name.includes('blocker')) return CALENDAR_TASK_TYPE_COLOR_MAP.BUG;
    if (name.includes('story')) return CALENDAR_TASK_TYPE_COLOR_MAP.STORY;
    if (name.includes('task')) return CALENDAR_TASK_TYPE_COLOR_MAP.TASK;
    return stringToColor(name || '');
  };

  // eslint-disable-next-line no-unused-vars
  const getStatusColor = (name) => {
    switch (name) {
      case 'Open': return COLORS.status.open;
      case 'In Progress': return COLORS.status.inProgress;
      case 'Review': return COLORS.status.review;
      case 'Done': return COLORS.status.done;
      case 'Closed': return COLORS.status.closed;
      case 'Blocked': return COLORS.status.blocked;
      default: return stringToColor(name || '');
    }
  };

  if (dropdownsLoading || projectDropdownsByUserLoading) {
    return (
      <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography>{t('taskModal.loadingDropdownData')}</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  const dropdownData = {
    types: dropdowns.types || [],
    priorities: dropdowns.priorities || [],
    statuses: dropdowns.statuses || [],
    projects: projectDropdownsByUser || [],
    assignees: dropdowns.assignees || [],
    labels: dropdowns.labels || [],
    sprints: dropdowns.sprints || []
  };

  // Adding null check for dropdownData.projects in AddTaskModal
  const selectedProject = (dropdownData.projects || []).find(x => x.id === formData.project);
  const projectSprintId = selectedProject?.scrum_sprint_id || preselectedSprintId;
  const projectSprintName = selectedProject?.scrum_sprint || preselectedSprintName;
  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: addTaskModalStyles.dialogPaper }}
    >
      <Box sx={addTaskModalStyles.header}>
        <Box display="flex" alignItems="center" gap={2}>
           <Box component="img" src={ICONS.createTask} sx={{ width: 40, height: 40 }} />
          <Box>
            <Typography sx={addTaskModalStyles.headerTitle}>
              {isEditMode ? t('taskModal.editTask', 'Edit Task') : t('taskModal.createNewTask', 'Create New Task')} 
              {selectedProject ? ` (${selectedProject.name})` : ''}
            </Typography>
            <Typography sx={addTaskModalStyles.headerSubtitle}>{t('taskModal.addNewTask', 'Add a new task to your project')}</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={addTaskModalStyles.content}>
        <TextField
          fullWidth
          label={t('taskModal.taskName', 'Task Name')}
          name="title"
          value={formData.title}
          onChange={(e) => {
            handleInputChange('title', e.target.value);
          }}
          onPaste={handleTitlePaste}
          required
          variant="outlined"
          sx={addTaskModalStyles.nameField}
          InputLabelProps={{ shrink: true }}
          placeholder={t('taskModal.enterTaskTitle', 'User Management')}
          inputProps={{ maxLength: CHARACTER_LIMITS.TICKET_TITLE }}
          error={!!titleError}
          helperText={titleError || `${formData.title.length}/${CHARACTER_LIMITS.TICKET_TITLE} chars`}
        />
        
        <Box sx={addTaskModalStyles.fieldRow}>
          {/* Assignee */}
          <Button
            sx={{
              ...addTaskModalStyles.fieldButton,
              ...(assigneeError ? {
                borderColor: COLORS.error,
                color: COLORS.error
              } : {})
            }}
            startIcon={<img src={ICONS.assignee} alt="assignee" style={{ width: 18, height: 18 }} />}
            onClick={(e) => handleMenuOpen(e, 'assignee')}
          >
            {formData.assignees?.length > 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                <OverflowTooltip
                  title={(() => {
                    const selected = formData.assignees
                      .map(id => (dropdownData.assignees || []).find(a => a.id === id))
                      .filter(Boolean);
                    if (selected.length === 0) return `${t('taskModal.assignee')}${isScrum ? '' : '*'}`;
                    if (selected.length === 1) return selected[0].name;
                    return `${selected[0].name} +${selected.length - 1}`;
                  })()}
                  tooltipWidth={360}
                  placement="top-start"
                  sx={{ fontSize: '14px', maxWidth: '100%' }}
                />
              </Box>
            ) : `${t('taskModal.assignee')}${isScrum ? '' : '*'}`}
          </Button>
          {/* Status */}
          <Button
            sx={addTaskModalStyles.fieldButton}
            startIcon={<img src={ICONS.status} alt="status" style={{ width: 18, height: 18 }} />}
            onClick={(e) => handleMenuOpen(e, 'status')}
          >
            {formData.status ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                <Typography variant="body2" sx={{ fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {dropdownData.statuses.find(s => s.id === formData.status)?.name}
                </Typography>
              </Box>
            ) : `${t('taskModal.status')}*`}
          </Button>
          <Menu 
            anchorEl={menuAnchors.status} 
            open={Boolean(menuAnchors.status)} 
            onClose={() => handleMenuClose('status')}
            PaperProps={{ style: { maxHeight: 250 } }}
            MenuListProps={{ autoFocusItem: false }}
          >
            {dropdownData.statuses.length > 0 ? (
              dropdownData.statuses.map(s => (
                <MenuItem key={s.id} onClick={() => handleMenuSelect('status', s.id)} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getStatusIcon(s.name) ? (
                    <img src={getStatusIcon(s.name)} alt="" style={{ width: 14, height: 14 }} />
                  ) : (
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: getStatusColor(s.name), flexShrink: 0 }} />
                  )}
                  <Typography sx={{ fontSize: '13px' }}>{s.name}</Typography>
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>{t('common.noOptions', 'No options available')}</MenuItem>
            )}
          </Menu>

          {/* Labels */}
          <Button
            sx={addTaskModalStyles.fieldButton}
            startIcon={<img src={ICONS.labels} alt="labels" style={{ width: 18, height: 18 }} />}
            onClick={(e) => handleMenuOpen(e, 'labels')}
          >
            {formData.labels?.length > 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                <Typography variant="body2" sx={{ fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {(() => {
                    const selectedLabels = formData.labels
                      .map(id => (dropdownData.labels || []).find(l => l.id === id))
                      .filter(Boolean);
                    if (selectedLabels.length === 0) return t('taskModal.labels');
                    if (selectedLabels.length === 1) return selectedLabels[0].name;
                    return `${selectedLabels[0].name} +${selectedLabels.length - 1}`;
                  })()}
                </Typography>
              </Box>
            ) : t('taskModal.labels')}
          </Button>
          <Menu
            anchorEl={menuAnchors.labels}
            open={Boolean(menuAnchors.labels)}
            onClose={() => { handleMenuClose('labels'); setIsCreatingLabel(false); setNewLabelName(''); setNewLabelColor(COLORS.primary); }}
            PaperProps={{ 
              style: { maxHeight: 250 },
              onMouseDown: (e) => { if (e.target.closest('.create-option')) e.preventDefault(); } 
            }}
            MenuListProps={{ autoFocusItem: false }}
          >
            {!isCreatingLabel && (
              <MenuItem className="create-option" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setIsCreatingLabel(true); }} sx={{ borderBottom: `1px solid ${COLORS.gray[200]}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: COLORS.primary }}>
                  <Add sx={{ fontSize: 16 }} />
                  <span>{t('taskModal.createNewLabel')}</span>
                </Box>
              </MenuItem>
            )}
            {isCreatingLabel && (
              <MenuItem disableRipple onMouseDown={(e) => e.preventDefault()} sx={{ borderBottom: `1px solid ${COLORS.gray[200]}`, py: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                  <TextField 
                    size="small" 
                    autoFocus 
                    placeholder={t('taskModal.enterLabelName')} 
                    value={newLabelName}
                    onChange={(e) => { e.stopPropagation(); setNewLabelName(e.target.value); }}
                    onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); handleCreateLabelConfirm(); } if (e.key === 'Escape') { e.preventDefault(); setIsCreatingLabel(false); } }}
                    onMouseDown={(e) => e.stopPropagation()} 
                    onClick={(e) => e.stopPropagation()}
                    inputProps={{ maxLength: CHARACTER_LIMITS.LABEL_NAME }}
                    helperText={`${newLabelName.length}/${CHARACTER_LIMITS.LABEL_NAME} characters`}
                    sx={{ 
                      flex: 1,
                      '& .MuiInputBase-root': { height: 32, fontSize: '13px' },
                      '& .MuiOutlinedInput-input': { py: 0 }
                    }} 
                  />
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      setCreateLabelAnchor(e.currentTarget);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="color-picker-preview"
                    sx={{
                      width: 32,
                      height: 32,
                      backgroundColor: newLabelColor,
                      
                    }}
                  />
                  <CustomColorPopover
                    open={Boolean(createLabelAnchor)}
                    anchorEl={createLabelAnchor}
                    onClose={() => setCreateLabelAnchor(null)}
                    colour={newLabelColor}
                    onChange={setNewLabelColor}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCreateLabelConfirm(); }} disabled={isCreatingLabelSubmitting || !newLabelName.trim() || newLabelName.length > CHARACTER_LIMITS.LABEL_NAME} sx={{ color: COLORS.success, p: 0.5 }}>
                      <Check sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setIsCreatingLabel(false); setNewLabelName(''); }} sx={{ color: COLORS.error, p: 0.5 }}>
                      <Clear sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Box>
              </MenuItem>
            )}
            {dropdownData.labels.length > 0 ? (
              dropdownData.labels.map(l => (
                <MenuItem key={l.id} onClick={() => {
                  if (editingLabel?.id === l.id) return;
                  const currentLabels = formData.labels || [];
                  const newLabels = currentLabels.includes(l.id) ? currentLabels.filter(id => id !== l.id) : [...currentLabels, l.id];
                  handleInputChange('labels', newLabels);
                }} sx={{ maxWidth: 300, overflow: 'hidden' }}>
                  {editingLabel?.id === l.id ? (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }} onClick={(e) => e.stopPropagation()}>
                      <TextField 
                        size="small" 
                        autoFocus 
                        value={editingLabel.newName}
                        onChange={(e) => { e.stopPropagation(); setEditingLabel(prev => ({ ...prev, newName: e.target.value })); }}
                        onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); handleUpdateLabel(); } if (e.key === 'Escape') { e.preventDefault(); setEditingLabel(null); } }}
                        onMouseDown={(e) => e.stopPropagation()} 
                        onClick={(e) => e.stopPropagation()}
                        inputProps={{ maxLength: CHARACTER_LIMITS.LABEL_NAME }}
                        helperText={`${editingLabel.newName.length}/${CHARACTER_LIMITS.LABEL_NAME} characters`}
                        sx={{ 
                          flex: 1,
                          '& .MuiInputBase-root': { height: 32, fontSize: '13px' },
                          '& .MuiOutlinedInput-input': { py: 0 }
                        }} 
                      />
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditLabelAnchor(e.currentTarget);
                        }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="color-picker-preview"
                    sx={{
                          width: 24,
                          height: 24,
                          backgroundColor: editingLabel.newColor,
                          
                        }}
                      />
                      <CustomColorPopover
                        open={Boolean(editLabelAnchor)}
                        anchorEl={editLabelAnchor}
                        onClose={() => setEditLabelAnchor(null)}
                        colour={editingLabel.newColor}
                        onChange={(color) => setEditingLabel(prev => ({ ...prev, newColor: color }))}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleUpdateLabel(); }} disabled={!editingLabel.newName?.trim() || editingLabel.newName.length > CHARACTER_LIMITS.LABEL_NAME} sx={{ color: COLORS.success, p: 0.5 }}>
                          <Check sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingLabel(null); }} sx={{ color: COLORS.error, p: 0.5 }}>
                          <Clear sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  ) : (
                    <Box display="flex" alignItems="center" gap={1} width="100%" overflow="hidden">
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: l.colour, flexShrink: 0 }} />
                      <Typography className="text-truncate" sx={{ fontSize: '13px', maxWidth: canManageTicketAttributes ? 'calc(100% - 60px)' : '100%' }}>
                        {l.name}
                      </Typography>
                      {formData.labels?.includes(l.id) && <Check fontSize="small" sx={{ ml: 1, flexShrink: 0 }} />}
                      
                      {canManageTicketAttributes && !l.status && (
                        <Box sx={{ display: 'flex', gap: 0.25, ml: 'auto' }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingLabel({ ...l, newName: l.name, newColor: l.colour || COLORS.primary });
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <Edit sx={{ fontSize: 14 }} />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              handleDeleteLabel(l); 
                            }} 
                            sx={{ p: 0.25 }}
                          >
                            <Delete sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  )}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>{t('common.noOptions', 'No options available')}</MenuItem>
            )}
          </Menu>

          {/* Sprint */}
          {(!isScrum || (isScrum && projectSprintId)) && (
            <>
              <Button
                sx={{ ...addTaskModalStyles.fieldButton, justifyContent: 'flex-start' }}
                startIcon={<img src={ICONS.sprint} alt="sprint" style={{ width: 18, height: 18 }} />}
                onClick={(e) => {
                  if (isScrum && projectSprintId) return;
                  handleMenuOpen(e, 'sprint');
                }}
                className={(isScrum && projectSprintId) ? 'sprint-field-disabled' : ''}
              >
                {isScrum && projectSprintId ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                    <Typography variant="body2" sx={{ fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {projectSprintName || 'Sprint'}
                    </Typography>
                  </Box>
                ) : formData.sprint ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                    <Typography variant="body2" sx={{ fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {dropdownData.sprints?.find(s => String(s.id) === String(formData.sprint))?.name || preselectedSprintName || 'Sprint'}
                    </Typography>
                  </Box>
                ) : t('taskModal.sprint')}
              </Button>
              {(!isScrum || !projectSprintId) && (
                <Menu
                  anchorEl={menuAnchors.sprint}
                  open={Boolean(menuAnchors.sprint)}
                  onClose={() => { handleMenuClose('sprint'); setIsCreatingSprint(false); setNewSprintName(''); }}
                  PaperProps={{ 
                    style: { maxHeight: 250 },
                    onMouseDown: (e) => { if (e.target.closest('.create-option')) e.preventDefault(); } 
                  }}
                  MenuListProps={{ autoFocusItem: false }}
                >
            {!isCreatingSprint && (
              <MenuItem className="create-option" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setIsCreatingSprint(true); }} sx={{ borderBottom: `1px solid ${COLORS.gray[200]}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: COLORS.primary }}>
                  <Add sx={{ fontSize: 16 }} />
                  <span>{t('taskModal.createNewSprint')}</span>
                </Box>
              </MenuItem>
            )}
            {isCreatingSprint && (
              <MenuItem disableRipple onMouseDown={(e) => e.preventDefault()} sx={{ borderBottom: `1px solid ${COLORS.gray[200]}`, py: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                  <TextField
                    size="small"
                    autoFocus
                    placeholder={t('taskModal.enterSprintName')}
                    value={newSprintName}
                    onChange={(e) => { e.stopPropagation(); setNewSprintName(e.target.value); }}
                    onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); handleCreateSprintConfirm(); } if (e.key === 'Escape') { e.preventDefault(); setIsCreatingSprint(false); } }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    inputProps={{ maxLength: CHARACTER_LIMITS.SPRINT_NAME }}
                    helperText={`${newSprintName.length}/${CHARACTER_LIMITS.SPRINT_NAME} characters`}
                    sx={{ 
                      flex: 1,
                      '& .MuiInputBase-root': { height: 32, fontSize: '13px' },
                      '& .MuiOutlinedInput-input': { py: 0 }
                    }} 
                  />
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      setCreateSprintAnchor(e.currentTarget);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="color-picker-preview"
                    sx={{
                      width: 32,
                      height: 32,
                      backgroundColor: newSprintColor,
                      
                    }}
                  />
                  <CustomColorPopover
                    open={Boolean(createSprintAnchor)}
                    anchorEl={createSprintAnchor}
                    onClose={() => setCreateSprintAnchor(null)}
                    colour={newSprintColor}
                    onChange={setNewSprintColor}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCreateSprintConfirm(); }} disabled={isCreatingSprintSubmitting || !newSprintName.trim() || newSprintName.length > CHARACTER_LIMITS.SPRINT_NAME} sx={{ color: COLORS.success, p: 0.5 }}>
                      <Check sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setIsCreatingSprint(false); setNewSprintName(''); }} sx={{ color: COLORS.error, p: 0.5 }}>
                      <Clear sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Box>
              </MenuItem>
            )}
            {dropdownData.sprints.length > 0 ? (
              dropdownData.sprints.map(s => (
                <MenuItem key={s.id} onClick={() => {
                  if (editingSprint?.id === s.id) return;
                  handleMenuSelect('sprint', s.id);
                }} sx={{ maxWidth: 300, overflow: 'hidden' }}>
                  {editingSprint?.id === s.id ? (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }} onClick={(e) => e.stopPropagation()}>
                      <TextField
                        size="small"
                        autoFocus
                        value={editingSprint.newName}
                        onChange={(e) => { e.stopPropagation(); setEditingSprint(prev => ({ ...prev, newName: e.target.value })); }}
                        onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); handleUpdateSprint(); } if (e.key === 'Escape') { e.preventDefault(); setEditingSprint(null); } }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        inputProps={{ maxLength: CHARACTER_LIMITS.SPRINT_NAME }}
                        helperText={`${editingSprint.newName.length}/${CHARACTER_LIMITS.SPRINT_NAME} characters`}
                        sx={{ 
                          flex: 1,
                          '& .MuiInputBase-root': { height: 32, fontSize: '13px' },
                          '& .MuiOutlinedInput-input': { py: 0 }
                        }} 
                      />
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditSprintAnchor(e.currentTarget);
                        }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="color-picker-preview"
                    sx={{
                          width: 24,
                          height: 24,
                          backgroundColor: editingSprint.newColor,
                          
                        }}
                      />
                      <CustomColorPopover
                        open={Boolean(editSprintAnchor)}
                        anchorEl={editSprintAnchor}
                        onClose={() => setEditSprintAnchor(null)}
                        colour={editingSprint.newColor}
                        onChange={(color) => setEditingSprint(prev => ({ ...prev, newColor: color }))}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleUpdateSprint(); }} disabled={!editingSprint.newName?.trim() || editingSprint.newName.length > CHARACTER_LIMITS.SPRINT_NAME} sx={{ color: COLORS.success, p: 0.5 }}>
                          <Check sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingSprint(null); }} sx={{ color: COLORS.error, p: 0.5 }}>
                          <Clear sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  ) : (
                    <Box display="flex" alignItems="center" gap={1} width="100%" overflow="hidden">
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.colour || s.color || COLORS.primary, flexShrink: 0 }} />
                      <Typography className="text-truncate" sx={{ fontSize: '13px', maxWidth: canManageTicketAttributes ? 'calc(100% - 60px)' : '100%' }}>
                        {s.name}
                      </Typography>
                      {formData.sprint === s.id && <Check fontSize="small" sx={{ ml: 1, flexShrink: 0 }} />}
                      
                      {canManageTicketAttributes && !s.status && (
                        <Box sx={{ display: 'flex', gap: 0.25, ml: 'auto' }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingSprint({ ...s, newName: s.name, newColor: s.colour || s.color || COLORS.primary });
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <Edit sx={{ fontSize: 14 }} />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              handleDeleteSprint(s); 
                            }} 
                            sx={{ p: 0.25 }}
                          >
                            <Delete sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  )}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>{t('common.noOptions', 'No options available')}</MenuItem>
            )}
          </Menu>
              )}
            </>
          )}

          {/* Task Type */}
          <Button
            sx={addTaskModalStyles.fieldButton}
            startIcon={<img src={ICONS.taskType} alt="type" style={{ width: 18, height: 18 }} />}
            onClick={(e) => handleMenuOpen(e, 'taskType')}
          >
            {formData.taskType ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                <Typography variant="body2" sx={{ fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {dropdownData.types.find(t => t.id === formData.taskType)?.name}
                </Typography>
              </Box>
            ) : `${t('taskModal.taskType')}*`}
          </Button>
          <Menu
            anchorEl={menuAnchors.taskType}
            open={Boolean(menuAnchors.taskType)}
            onClose={() => { handleMenuClose('taskType'); setIsCreatingTaskType(false); setNewTaskTypeName(''); }}
            PaperProps={{ 
              style: { maxHeight: 250 },
              onMouseDown: (e) => { if (e.target.closest('.create-option')) e.preventDefault(); } 
            }}
            MenuListProps={{ autoFocusItem: false }}
          >
            {!isCreatingTaskType && (
              <MenuItem className="create-option" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setIsCreatingTaskType(true); }} sx={{ borderBottom: `1px solid ${COLORS.gray[200]}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: COLORS.primary }}>
                  <Add sx={{ fontSize: 16 }} />
                  <span>{t('taskModal.createNewTaskType')}</span>
                </Box>
              </MenuItem>
            )}
            {isCreatingTaskType && (
              <MenuItem disableRipple onMouseDown={(e) => e.preventDefault()} sx={{ borderBottom: `1px solid ${COLORS.gray[200]}` }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, width: '100%' }}>
                  <TextField
                    size="small" autoFocus
                    placeholder={t('taskModal.enterTaskTypeName')}
                    value={newTaskTypeName}
                    onChange={(e) => { e.stopPropagation(); setNewTaskTypeName(e.target.value); }}
                    onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); handleCreateTaskTypeConfirm(); } if (e.key === 'Escape') { e.preventDefault(); handleCreateTaskTypeCancel(); } }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    inputProps={{ maxLength: CHARACTER_LIMITS.TASK_TYPE_NAME }}
                    helperText={`${newTaskTypeName.length}/${CHARACTER_LIMITS.TASK_TYPE_NAME} characters`}
                    sx={{ flex: 1 }}
                  />
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCreateTaskTypeConfirm(); }} disabled={isCreatingTaskTypeSubmitting || !newTaskTypeName.trim() || !formData.project || newTaskTypeName.length > CHARACTER_LIMITS.TASK_TYPE_NAME} sx={{ color: COLORS.success, mt: 0.25 }}>
                    <Check sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCreateTaskTypeCancel(); }} sx={{ color: COLORS.error, mt: 0.25 }}>
                    <Clear sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </MenuItem>
            )}
            {dropdownData.types.length > 0 ? (
              dropdownData.types.map(typeItem => (
                <MenuItem key={typeItem.id} onClick={() => {
                  if (editingTaskType?.id === typeItem.id) return;
                  handleMenuSelect('taskType', typeItem.id);
                }} sx={{ maxWidth: 300, overflow: 'hidden' }}>
                  {editingTaskType?.id === typeItem.id ? (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, width: '100%' }} onClick={(e) => e.stopPropagation()}>
                      <TextField
                        size="small" autoFocus
                        value={editingTaskType.newName}
                        onChange={(e) => { e.stopPropagation(); setEditingTaskType(prev => ({ ...prev, newName: e.target.value })); }}
                        onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); handleUpdateTaskType(); } if (e.key === 'Escape') { e.preventDefault(); setEditingTaskType(null); } }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        inputProps={{ maxLength: CHARACTER_LIMITS.TASK_TYPE_NAME }}
                        helperText={`${editingTaskType.newName.length}/${CHARACTER_LIMITS.TASK_TYPE_NAME} characters`}
                        sx={{ flex: 1 }}
                      />
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleUpdateTaskType(); }} disabled={!editingTaskType.newName?.trim() || editingTaskType.newName.length > CHARACTER_LIMITS.TASK_TYPE_NAME} sx={{ color: COLORS.success, mt: 0.25 }}>
                        <Check sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingTaskType(null); }} sx={{ color: COLORS.error, mt: 0.25 }}>
                        <Clear sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box display="flex" alignItems="center" gap={1} width="100%" overflow="hidden">
                      {getTaskTypeIcon(typeItem.name) ? (
                        <img src={getTaskTypeIcon(typeItem.name)} alt="" style={{ width: 14, height: 14 }} />
                      ) : (
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: getTaskTypeColor(typeItem.name), flexShrink: 0 }} />
                      )}
                      <Typography className="text-truncate" sx={{ fontSize: '13px', maxWidth: canManageTicketAttributes ? 'calc(100% - 60px)' : '100%' }}>
                        {typeItem.name}
                      </Typography>
                      {formData.taskType === typeItem.id && <Check fontSize="small" sx={{ ml: 1, flexShrink: 0 }} />}
                      
                      {canManageTicketAttributes && !typeItem.status && (
                        <Box sx={{ display: 'flex', gap: 0.25, ml: 'auto' }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingTaskType({ ...typeItem, newName: typeItem.name });
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <Edit sx={{ fontSize: 14 }} />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              handleDeleteTaskType(typeItem); 
                            }} 
                            sx={{ p: 0.25 }}
                          >
                            <Delete sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  )}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>{t('common.noOptions', 'No options available')}</MenuItem>
            )}
          </Menu>

          {/* Priority */}
          <Button
            sx={addTaskModalStyles.fieldButton}
            startIcon={<img src={ICONS.priority} alt="priority" style={{ width: 18, height: 18 }} />}
            onClick={(e) => handleMenuOpen(e, 'priority')}
          >
            {formData.priority ? (dropdownData.priorities.find(p => p.id === formData.priority)?.name || t('taskModal.priority')) : `${t('taskModal.priority')}*`}
          </Button>
          <Menu
            anchorEl={menuAnchors.priority}
            open={Boolean(menuAnchors.priority)}
            onClose={() => { handleMenuClose('priority'); setIsCreatingPriority(false); setNewPriorityName(''); }}
            PaperProps={{ 
              style: { maxHeight: 250 },
              onMouseDown: (e) => { if (e.target.closest('.create-option')) e.preventDefault(); } 
            }}
            MenuListProps={{ autoFocusItem: false }}
          >
            {!isCreatingPriority && (
              <MenuItem className="create-option" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setIsCreatingPriority(true); }} sx={{ borderBottom: `1px solid ${COLORS.gray[200]}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: COLORS.primary }}>
                  <Add sx={{ fontSize: 16 }} />
                  <span>{t('taskModal.createNewPriority')}</span>
                </Box>
              </MenuItem>
            )}
            {isCreatingPriority && (
              <MenuItem disableRipple onMouseDown={(e) => e.preventDefault()} sx={{ borderBottom: `1px solid ${COLORS.gray[200]}` }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, width: '100%' }}>
                  <TextField
                    size="small" autoFocus
                    placeholder={t('taskModal.enterPriorityName')}
                    value={newPriorityName}
                    onChange={(e) => { e.stopPropagation(); setNewPriorityName(e.target.value); }}
                    onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); handleCreatePriorityConfirm(); } if (e.key === 'Escape') { e.preventDefault(); handleCreatePriorityCancel(); } }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    inputProps={{ maxLength: CHARACTER_LIMITS.PRIORITY_NAME }}
                    helperText={`${newPriorityName.length}/${CHARACTER_LIMITS.PRIORITY_NAME} characters`}
                    sx={{ 
                      flex: 1,
                      '& .MuiInputBase-root': { height: 32, fontSize: '13px' },
                      '& .MuiOutlinedInput-input': { py: 0 }
                    }} 
                  />
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      setCreatePriorityAnchor(e.currentTarget);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="color-picker-preview"
                    sx={{
                      width: 32,
                      height: 32,
                      backgroundColor: newPriorityColor,
                      
                    }}
                  />
                  <CustomColorPopover
                    open={Boolean(createPriorityAnchor)}
                    anchorEl={createPriorityAnchor}
                    onClose={() => setCreatePriorityAnchor(null)}
                    colour={newPriorityColor}
                    onChange={setNewPriorityColor}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCreatePriorityConfirm(); }} disabled={isCreatingPrioritySubmitting || !newPriorityName.trim() || !formData.project || newPriorityName.length > CHARACTER_LIMITS.PRIORITY_NAME} sx={{ color: COLORS.success, p: 0.5 }}>
                      <Check sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCreatePriorityCancel(); }} sx={{ color: COLORS.error, p: 0.5 }}>
                      <Clear sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Box>
              </MenuItem>
            )}
            {dropdownData.priorities.length > 0 ? (
              dropdownData.priorities.map(p => (
                <MenuItem key={p.id} value={p.id} onClick={() => {
                  if (editingPriority?.id === p.id) return;
                  handleMenuSelect('priority', p.id);
                }} sx={{ maxWidth: 300, overflow: 'hidden' }}>
                  {editingPriority?.id === p.id ? (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, width: '100%' }} onClick={(e) => e.stopPropagation()}>
                      <TextField
                        size="small" autoFocus
                        value={editingPriority.newName}
                        onChange={(e) => { e.stopPropagation(); setEditingPriority(prev => ({ ...prev, newName: e.target.value })); }}
                        onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); handleUpdatePriority(); } if (e.key === 'Escape') { e.preventDefault(); setEditingPriority(null); } }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        inputProps={{ maxLength: CHARACTER_LIMITS.PRIORITY_NAME }}
                        helperText={`${editingPriority.newName.length}/${CHARACTER_LIMITS.PRIORITY_NAME} characters`}
                        sx={{ 
                          flex: 1,
                          '& .MuiInputBase-root': { height: 32, fontSize: '13px' },
                          '& .MuiOutlinedInput-input': { py: 0 }
                        }}
                      />
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditPriorityAnchor(e.currentTarget);
                        }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="color-picker-preview"
                    sx={{
                          width: 24,
                          height: 24,
                          backgroundColor: editingPriority.newColor,
                          
                        }}
                      />
                      <CustomColorPopover
                        open={Boolean(editPriorityAnchor)}
                        anchorEl={editPriorityAnchor}
                        onClose={() => setEditPriorityAnchor(null)}
                        colour={editingPriority.newColor}
                        onChange={(color) => setEditingPriority(prev => ({ ...prev, newColor: color }))}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleUpdatePriority(); }} disabled={!editingPriority.newName?.trim() || editingPriority.newName.length > CHARACTER_LIMITS.PRIORITY_NAME} sx={{ color: COLORS.success, p: 0.5 }}>
                          <Check sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingPriority(null); }} sx={{ color: COLORS.error, p: 0.5 }}>
                          <Clear sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  ) : (
                    <Box display="flex" alignItems="center" gap={1} width="100%" overflow="hidden">
                      <Flag sx={{ color: getPriorityColor(p, { priorities: dropdownData.priorities }), mr: 1, fontSize: 16, flexShrink: 0 }} />
                      <Typography className="text-truncate" sx={{ fontSize: '13px', maxWidth: canManageTicketAttributes ? 'calc(100% - 60px)' : '100%' }}>
                        {p.name}
                      </Typography>
                      {formData.priority === p.id && <Check fontSize="small" sx={{ ml: 1, flexShrink: 0 }} />}
                      
                      {canManageTicketAttributes && !p.status && (
                        <Box sx={{ display: 'flex', gap: 0.25, ml: 'auto' }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingPriority({ ...p, newName: p.name, newColor: p.colour || COLORS.primary });
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <Edit sx={{ fontSize: 14 }} />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              handleDeletePriority(p); 
                            }} 
                            sx={{ p: 0.25 }}
                          >
                            <Delete sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  )}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>{t('common.noOptions', 'No options available')}</MenuItem>
            )}
          </Menu>

          <Menu 
            anchorEl={menuAnchors.assignee} 
            open={Boolean(menuAnchors.assignee)} 
            onClose={() => handleMenuClose('assignee')}
            PaperProps={{ style: { maxHeight: 250 } }}
            MenuListProps={{ autoFocusItem: false }}
          >
             {dropdownData.assignees.length > 0 ? (
               dropdownData.assignees.map(a => (
                <MenuItem key={a.id} onClick={() => {
                  const currentAssignees = formData.assignees || [];
                  const newAssignees = currentAssignees.includes(a.id) 
                    ? currentAssignees.filter(id => id !== a.id) 
                    : [...currentAssignees, a.id];
                  handleInputChange('assignees', newAssignees);
                }} sx={{ maxWidth: 300, overflow: 'hidden' }}>
                  <Box display="flex" alignItems="center" gap={1} width="100%" overflow="hidden">
                    <Avatar sx={{ width: 24, height: 24, fontSize: '11px', mr: 1, flexShrink: 0, bgcolor: stringToColor(a.name) }}>
                      {getInitials(a.name)}
                    </Avatar>
                    <OverflowTooltip
                      title={a.name}
                      tooltipWidth={360}
                      placement="top-start"
                      className="overflow-tooltip-dropdown"
                      sx={{ fontSize: '14px', maxWidth: 'calc(100% - 30px)' }}
                    />
                    {formData.assignees?.includes(a.id) && <Check fontSize="small" sx={{ ml: 'auto', flexShrink: 0 }} />}
                  </Box>
                </MenuItem>
               ))
             ) : (
               <MenuItem disabled>{t('common.noOptions', 'No options available')}</MenuItem>
             )}
          </Menu>
          {assigneeError && (
            <Typography sx={{ color: COLORS.error, fontSize: '12px', mt: -0.5, width: '100%' }}>
              {assigneeError}
            </Typography>
          )}

          {/* Due Date & Time */}
          <>
            <Button
              sx={addTaskModalStyles.fieldButton}
              startIcon={<img src={ICONS.dueDate} alt="due date" style={{ width: 18, height: 18 }} />}
              onClick={(e) => {
                setDateError('');
                setDueDatePickerAnchor(e.currentTarget);
              }}
            >
              {formData.dueDate ? (new Date(formData.dueDate).toLocaleDateString && !isNaN(new Date(formData.dueDate)) ? new Date(formData.dueDate).toLocaleDateString() : t('common.notAvailable')) : t('common.dueDateTime')}
              {formData.dueTime ? ` ${format12h(formData.dueTime)}` : ''}
            </Button>
            
            <DatePickerModal
              open={Boolean(dueDatePickerAnchor)}
              anchorEl={dueDatePickerAnchor}
              onClose={() => setDueDatePickerAnchor(null)}
              hideStartDate={true}
              showTime={true}
              allowSingleSelection={true}
              selectedDueDate={formData.dueDate || (() => {
                const d = new Date();
                return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
              })()}
              selectedDueTime={formData.dueTime}
              onDateChange={(dates) => {}}
              onApply={(res) => {
                setFormData(prev => {
                  const nextData = { ...prev };
                  if (res.toDate) nextData.dueDate = res.toDate;
                  if (res.dueTime !== undefined) nextData.dueTime = res.dueTime;
                  validateDates(nextData.startDate, nextData.dueDate, nextData.startTime, nextData.dueTime);
                  return nextData;
                });
              }}
              onClearDueDate={() => {
                setFormData(prev => {
                  const nextData = { ...prev, dueDate: '', dueTime: '' };
                  validateDates(nextData.startDate, nextData.dueDate, nextData.startTime, nextData.dueTime);
                  return nextData;
                });
              }}
            />
          </>

          {/* Story Points / Time Estimate */}
          {isScrum ? (
            <>
              {!showStoryPoints ? (
                <Button
                  sx={addTaskModalStyles.fieldButton}
                  startIcon={<img src={ICONS.timeEstimate} alt="time" style={{ width: 18, height: 18 }} />}
                  onClick={() => setShowStoryPoints(true)}
                >
                  {t('taskModal.storyPoints')}{formData.sprint ? '*' : ''}
                </Button>
              ) : (
                <Box sx={{ position: 'relative', width: '140px' }}>
                  <TextField
                    autoFocus
                    type="number"
                    size="small"
                    inputProps={{ min: 1, max: 40 }}
                    sx={{ 
                      width: '100%', 
                      '& .MuiOutlinedInput-root': { height: '40px', borderRadius: '8px' },
                      '& .MuiFormHelperText-root': { display: 'none' }
                    }}
                    placeholder="e.g. 3"
                    value={formData.storyPoints}
                    onChange={(e) => handleInputChange('storyPoints', e.target.value.replace(REGEX.nonNumeric, ''))}
                    onBlur={() => validateStoryPoints()}
                    error={!!storyPointsError}
                    onKeyDown={(e) => { if (RESTRICTED_NUMBER_KEYS.includes(e.key)) e.preventDefault(); }}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                          <img src={ICONS.timeEstimate} alt="estimate" style={{ width: 16, height: 16 }} />
                        </Box>
                      ),
                      endAdornment: (
                        <IconButton size="small" onClick={() => { handleInputChange('storyPoints', ''); setShowStoryPoints(false); setStoryPointsError(''); }} sx={{ p: 0.25 }}>
                          <Close sx={{ fontSize: 14 }} />
                        </IconButton>
                      )
                    }}
                  />
                  {storyPointsError && (
                    <Typography
                      color="error"
                      sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        fontSize: '11px',
                        mt: 0.5,
                        width: '220px'
                      }}
                    >
                      {storyPointsError}
                    </Typography>
                  )}
                </Box>
              )}
            </>
          ) : (
            <>
              {!showTimeEstimate ? (
                <Button
                  sx={addTaskModalStyles.fieldButton}
                  startIcon={<img src={ICONS.timeEstimate} alt="time" style={{ width: 18, height: 18 }} />}
                  onClick={() => setShowTimeEstimate(true)}
                >
                  {t('taskModal.timeEstimate', 'Time Estimate')}
                </Button>
              ) : (
                <Box sx={{ position: 'relative', width: '140px' }}>
                  <TextField
                    autoFocus
                    size="small"
                    sx={{ 
                      width: '100%', 
                      '& .MuiOutlinedInput-root': { height: '40px', borderRadius: '8px' },
                      '& .MuiFormHelperText-root': { display: 'none' }
                    }}
                    placeholder={t('taskModal.estimatePlaceholder', '0h 0m')}
                    value={formData.estimateTime}
                    onChange={(e) => handleInputChange('estimateTime', e.target.value)}
                    error={!!timeError}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                          <img src={ICONS.timeEstimate} alt="estimate" style={{ width: 16, height: 16 }} />
                        </Box>
                      ),
                      endAdornment: (
                        <IconButton size="small" onClick={() => { handleInputChange('estimateTime', ''); setShowTimeEstimate(false); setTimeError(''); }} sx={{ p: 0.25 }}>
                          <Close sx={{ fontSize: 14 }} />
                        </IconButton>
                      )
                    }}
                  />
                  {timeError && (
                    <Typography 
                      color="error" 
                      sx={{ 
                        position: 'absolute', 
                        top: '100%', 
                        left: 0, 
                        fontSize: '11px',
                        mt: 0.5,
                        width: '200px'
                      }}
                    >
                      {timeError}
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}

          <Button
            sx={addTaskModalStyles.fieldButton}
            startIcon={<img src={ICONS.createChecklist} alt="checklist" style={{ width: 18, height: 18 }} />}
            onClick={() => setShowChecklist(!showChecklist)}
          >
            {t('taskModal.checklist', 'Checklist')}
          </Button>

          <Button
            sx={{
              ...addTaskModalStyles.fieldButton,
              ...(showRecurrence ? {
                borderColor: COLORS.ui.ghostBorder,
                color: COLORS.brand.accent,
                bgcolor: COLORS.ui.ghostBg
              } : {})
            }}
            startIcon={<Repeat sx={{ fontSize: 18 }} />}
            onClick={() => setShowRecurrence(true)}
          >
            Recurring
          </Button>
        </Box>

        {dateError && (
          <Typography color="error" sx={{ mt: 1, mb: 0, fontSize: '12px' }}>
            {dateError}
          </Typography>
        )}

        {showRecurrence && (
          <RecurrencePanel
            isRecurring={formData.is_recurring}
            recurrenceConfig={formData.recurrence_config}
            initialRecurrenceConfig={isEditMode ? taskData?.recurrence_config : undefined}
            nextRepOn={formData.next_rep_on}
            onChange={({ isRecurring, recurrenceConfig }) => {
              setFormData((prev) => ({
                ...prev,
                is_recurring: isRecurring,
                recurrence_config: isRecurring ? recurrenceConfig : null
              }));
            }}
          />
        )}

        {!showDescription && !showAC ? (
          <Box sx={{ display: 'flex', gap: 2, flexDirection: 'row', flexWrap: 'wrap' }}>
            <Button
              sx={addTaskModalStyles.thickButton}
              startIcon={<Add />}
              onClick={() => setShowDescription(true)}
            >
              {t('taskModal.addDescription', 'Add Description*')}
            </Button>
            <Button
              sx={addTaskModalStyles.thickButton}
              startIcon={<Add />}
              onClick={() => setShowAC(true)}
            >
              {t('taskModal.addAcceptanceCriteria', 'Add Acceptance Criteria*')}
            </Button>
          </Box>
        ) : !showDescription ? (
          <Box sx={addTaskModalStyles.richTextActionRow}>
            <Button
              sx={addTaskModalStyles.thickButton}
              startIcon={<Add />}
              onClick={() => setShowDescription(true)}
            >
              {t('taskModal.addDescription', 'Add Description*')}
            </Button>
          </Box>
        ) : (
          <Box sx={addTaskModalStyles.richTextSection}>
            <Box sx={addTaskModalStyles.richTextLabel('description')}>
              <Add sx={addTaskModalStyles.richTextLabelIcon} />
              <Typography component="span" sx={{ font: 'inherit' }}>
                {t('taskModal.addDescription', 'Add Description*')}
              </Typography>
            </Box>
            <CommentBox
              ref={descriptionCommentBoxRef}
              initialContent={prepareContentForEditing(formData.description)}
              onSubmit={(content) => handleInputChange('description', content)}
              onCancel={() => setShowDescription(false)}
              hideActions={true}
              mentionUsers={dropdowns?.assignees || []}
              ticketId={draftId}
              fieldName="description"
              enableAutoSave={false}
              autoFocusEditor={true}
            />
          </Box>
        )}

        {!showAC && showDescription ? (
          <Box sx={addTaskModalStyles.richTextActionRow}>
            <Button
              sx={addTaskModalStyles.thickButton}
              startIcon={<Add />}
              onClick={() => setShowAC(true)}
            >
              {t('taskModal.addAcceptanceCriteria', 'Add Acceptance Criteria*')}
            </Button>
          </Box>
        ) : showAC ? (
          <Box sx={addTaskModalStyles.richTextSection}>
            <Box sx={addTaskModalStyles.richTextLabel('acceptance')}>
              <Add sx={addTaskModalStyles.richTextLabelIcon} />
              <Typography component="span" sx={{ font: 'inherit' }}>
                {t('taskModal.addAcceptanceCriteria', 'Add Acceptance Criteria*')}
              </Typography>
            </Box>
            <CommentBox
              initialContent={prepareContentForEditing(formData.acceptanceCriteria)}
              onSubmit={(content) => handleInputChange('acceptanceCriteria', content)}
              onCancel={() => setShowAC(false)}
              hideAttachments={true}
              hideActions={true}
              ticketId={draftId}
              fieldName="acceptanceCriteria"
              enableAutoSave={false}
              autoFocusEditor={true}
            />
          </Box>
        ) : null}

        {/* Checklist Section Integration */}
        {showChecklist && (
          <Box sx={{ mt: 3 }}>
            <ChecklistManager
              value={formData.checklist}
              onChange={(checklist) => handleInputChange('checklist', checklist)}
              onClose={() => setShowChecklist(false)}
              isCreateMode={true}
              projectId={normalizedFormProjectId || selectedProjectId}
            />
          </Box>
        )}
      </DialogContent>

      <Box sx={addTaskModalStyles.footer}>
        {/* Left side: Selected files */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', maxWidth: '60%' }}>
          {formData.attachments?.map((file, index) => {
            const name = file.name || (typeof file === 'string' ? file.split('/').pop() : 'Attachment');
            const url = file.url || (typeof file === 'string' ? file : null);
            const isImage = url && /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(url);
            return (
              <Box
                key={index}
                sx={{
                  position: 'relative',
                  width: 44,
                  height: 44,
                  borderRadius: '6px',
                  border: `1px solid ${COLORS.gray[200]}`,
                  overflow: 'visible',
                  flexShrink: 0,
                  bgcolor: COLORS.gray[50],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isImage ? (
                  <img
                    src={url}
                    alt={name}
                    style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: '6px', display: 'block' }}
                  />
                ) : (
                  <Tooltip title={name}>
                    <InsertDriveFile sx={{ fontSize: 22, color: COLORS.gray[400] }} />
                  </Tooltip>
                )}
                <IconButton
                  size="small"
                  onClick={() => removeAttachment(index)}
                  sx={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    width: 16,
                    height: 16,
                    bgcolor: COLORS.gray[600],
                    color: COLORS.white,
                    p: 0,
                    '&:hover': { bgcolor: COLORS.error },
                    '& .MuiSvgIcon-root': { fontSize: 10 }
                  }}
                >
                  <Close />
                </IconButton>
              </Box>
            );
          })}
          {isUploadingFiles && <CircularProgress size={16} sx={{ ml: 0.5 }} />}
        </Box>

        {/* Right side: Attachment icon and Create button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <input
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.svg,.webp,.zip,.mp4,.mov,.avi,.webm,.mkv"
            id="task-footer-attachment"
            multiple
            hidden
            onChange={handleFileUpload}
          />
          <IconButton 
            size="small" 
            onClick={() => document.getElementById('task-footer-attachment').click()}
            sx={{ border: `1px solid ${COLORS.gray[200]}`, borderRadius: '8px', p: 1 }}
          >
            <img src={ICONS.attachFile} alt="attach" style={{ width: 20, height: 20 }} />
          </IconButton>
          
          <Button 
            variant="contained" 
            onClick={handleSubmit} 
            disabled={!formData.title.trim() || isSubmitting || isUploadingFiles || !!dateError || !!titleError || !hasEditFormChanges || !isRecurrenceComplete(formData.is_recurring, formData.recurrence_config)}
            sx={addTaskModalStyles.createButton}
          >
            {isSubmitting ? (isEditMode ? t('taskModal.updating', 'Updating...') : t('taskModal.creating', 'Creating...')) : (isEditMode ? t('taskModal.updateTask', 'Update Task') : t('taskModal.createTask', 'Create Task'))}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default AddTaskModal;
